import { Component, OnInit, ViewChild } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { MatSidenav } from '@angular/material/sidenav';
import { Dataset } from 'src/app/models/dataset.model';
import { DatasetsService } from 'src/app/services/datasets.service';
import * as globals from '../../globals';
import saveAs from 'file-saver';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'src/app/shared/confirmation-dialog/confirmation-dialog.component';
import { first, firstValueFrom } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ClassesService } from 'src/app/services/classes.service';
import { LoggingService } from '../../../services/logging.service';

@Component({
  standalone: false,
  selector: 'app-dataset-list',
  templateUrl: './dataset-list.component.html',
  styleUrls: ['./dataset-list.component.scss']
})
export class DatasetListComponent implements OnInit {

  constructor(
    public media: MediaObserver,
    public dialog: MatDialog,
    private snackBar: MatSnackBar,
    private classesService: ClassesService,
    private datasetsService: DatasetsService,
    private logger: LoggingService) { }

  ngOnInit(): void {
    this.getDatasets();
    this.isAdministrator = (localStorage.getItem(globals.POLICIES) === `${globals.TC}:${globals.trainingadmin}`);
  }

  private updateDataset(dataset: Dataset)
  {
    this.dataset = dataset;
    this.sidenavdataset.open();
  }

  private async getDatasets() {
    try {
      this.loadingSpinner(true);
      this.datasetsService.get_datasets().subscribe({
        next: (data) => {
          if (data !== undefined) {
            this.logger.logEvent('Dataset List Fetch Success', {datasetCount: data?.length});
            this.dataSource = new MatTableDataSource(data);
          }
          this.loadingSpinner(false);
        },
        error: (e) => {
          this.logger.logException(e);
          this.loadingSpinner(false);
        }
      });
    }
    catch {}
  }

  private async downloadFile(filename: string) {
    try {
      this.loadingSpinner(true);

      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          datasetTd: this.dataset?.id,
          dialogTitle: globals.DOWNLOAD_DATASET,
          description: `${globals.DOWNLOAD_DATASET_CONFIRM} ${filename}?`,
          cautionaryMessage: '',
          okButtonTitle: globals.DOWNLOAD,
        },
        width: '500px'
      });

      const result = await firstValueFrom(dialogRef
        .afterClosed()
        .pipe(first()));

      if (result) {
        this.logger.logEvent('Dataset Download Success', { fileName: filename });
        this.datasetsService.dataset_download(filename).subscribe({
          next: (data) => {
            const blob = new Blob([data], { type: 'application/x-zip-compressed' });
            saveAs(blob, filename);
            this.loadingSpinner(false);
          },
          error: (e) => {
            this.logger.logException(e);
            this.loadingSpinner(false);
          }
        });
      }
      else {
        this.loadingSpinner(false);
      }
    }
    catch (exception) {
      this.logger.logException(exception);
    }
  }

  private async deleteDataset(dataset: Dataset) {
    try {
      this.loadingSpinner(true);

      const isClassExists_Dataset = await firstValueFrom(this.classesService.isClassExists_Dataset(dataset?.id));

      if(isClassExists_Dataset) {
        const message = globals.DATASET_MAPPEDWITHCLASS.replace('{0}', dataset.name);
        this.displaySnackBar(message);
        this.loadingSpinner(false);
        return;
      }
            
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          datasetTd: this.dataset?.id,
          dialogTitle: 'Delete dataset',
          description: `${globals.DELETE_WARN} dataset ${dataset.name}?`,
          cautionaryMessage: '',
          okButtonTitle: globals.DELETE,
        },
        width: '500px'
      });
  
      const result = await firstValueFrom(dialogRef
        .afterClosed()
        .pipe(first()));
      
      if(result) {

        const response = await firstValueFrom(this.datasetsService.delete_dataset(dataset.id));

        if (response) {
          this.logger.logEvent('Dataset Delete Success', {datasetId: dataset?.id});
          const file_response = await firstValueFrom(this.datasetsService.delete_dataset_file(dataset.file));
          this.displaySnackBar(globals.datasetDelete_success);
          await this.getDatasets();
        } 
        else {
          this.displaySnackBar(globals.datasetDelete_fail);
          this.loadingSpinner(false);
        }
      }
      else {
        this.loadingSpinner(false);
      }
    }
    catch (exception) {
      this.logger.logException(exception);
      this.loadingSpinner(false);
    }
  }

  addedDataset = (response) => this.refresh();

  cancelledDataset = (response) => this.refresh();

  private refresh = () => {
    this.dataset = new Dataset();
    this.getDatasets();
  };

  isMobile = () => window.matchMedia('(max-width: 840px)').matches;

  loadingSpinner = (value: boolean) => this.isLoading = value;
  
  displaySnackBar = (message: string) => this.snackBar.open(message, null, {duration: 3000});

  isAdministrator: boolean;
  isLoading: boolean;
  dataset: Dataset = new Dataset();
  dataSource = new MatTableDataSource<Dataset>();
  displayedColumns: string[] = globals.dataset_columns;
  
  @ViewChild('sidenavdataset') sidenavdataset: MatSidenav;
  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

}
